import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Slider } from '@/components/ui/slider'
import { CurrencyIcon } from '@/components/profile/CurrencyIcon'
import { formatBalance } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import { useGetCurrenciesQuery } from '@/store/api/paymentApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectMineGame, setMineAutoBetSettings, setMineBetAmount, setMineBetMode, setMineCount } from '@/store/slices/mineSlice'
import { MINE_BOARD_SIZE } from '@/config/original'
import type { MineBetMode } from '@/types/mine'
import type { GameController } from '@/components/original/games/mine/core/GameController'

const MIN_MINES = 3
const MAX_MINES = 24

interface MineBetPanelProps {
	controller: GameController | null
	className?: string
}

export function MineBetPanel({ controller, className }: MineBetPanelProps) {
	const dispatch = useAppDispatch()
	const mine = useAppSelector(selectMineGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: currenciesResponse } = useGetCurrenciesQuery()
	const [amountDraft, setAmountDraft] = useState(mine.betAmount)
	const [autoBetCountDraft, setAutoBetCountDraft] = useState(String(mine.autoBetSettings.betCount))
	const [stopOnProfitDraft, setStopOnProfitDraft] = useState(String(mine.autoBetSettings.stopOnProfit))
	const [stopOnLossDraft, setStopOnLossDraft] = useState(String(mine.autoBetSettings.stopOnLoss))

	const currencies = currenciesResponse?.data ?? []
	const currency = useMemo(() => currencies.find((item) => item.currency_code.toUpperCase() === mine.currencyCode.toUpperCase()), [currencies, mine.currencyCode])

	const usdAmount = useMemo(() => {
		const amount = Number(amountDraft)
		const rate = Number(currency?.exchange_rate ?? 0)

		if (!Number.isFinite(amount) || !Number.isFinite(rate)) {
			return 0
		}

		return amount * rate
	}, [amountDraft, currency?.exchange_rate])

	const revealedGemCount = mine.revealedTiles.filter((tile) => tile.isRevealed && tile.content === 'gem').length
	const remainingSafeTiles = MINE_BOARD_SIZE - mine.mineCount - revealedGemCount
	const controlsDisabled = mine.phase === 'live' || mine.isSubmitting || mine.autoBetRunning
	const isAutoMode = mine.betMode === 'auto'
	const isLive = mine.phase === 'live'

	useEffect(() => {
		setAmountDraft(mine.betAmount)
	}, [mine.betAmount])

	useEffect(() => {
		setAutoBetCountDraft(String(mine.autoBetSettings.betCount))
	}, [mine.autoBetSettings.betCount])

	const commitAmount = useCallback(() => {
		const parsed = Number(amountDraft)
		if (!Number.isFinite(parsed) || parsed <= 0) {
			setAmountDraft(mine.betAmount)
			return
		}

		dispatch(setMineBetAmount(parsed.toString()))
	}, [amountDraft, dispatch, mine.betAmount])

	const handleQuickAmount = useCallback(
		(multiplier: number) => {
			const parsed = Number(amountDraft) || Number(mine.betAmount) || 0
			const next = Math.max(0, parsed * multiplier)
			const nextAmount = next > 0 ? String(next) : '0'

			setAmountDraft(nextAmount)
			dispatch(setMineBetAmount(nextAmount))
		},
		[amountDraft, dispatch, mine.betAmount],
	)

	const handleModeChange = useCallback(
		(value: string) => {
			if (mine.autoBetRunning) {
				return
			}

			dispatch(setMineBetMode(value as MineBetMode))
		},
		[dispatch, mine.autoBetRunning],
	)

	const action = useMemo(() => {
		if (!isAuthenticated) {
			return { label: 'Bet', variant: 'secondary' as const, disabled: true, onClick: () => undefined }
		}

		if (isAutoMode) {
			if (mine.autoBetRunning) {
				return {
					label: 'Stop Autobet',
					variant: 'destructive' as const,
					disabled: false,
					onClick: () => controller?.stopAutoBet(),
				}
			}

			return {
				label: 'Start Autobet',
				variant: 'default' as const,
				disabled: mine.isSubmitting || mine.autoSelectedPoints.length < 1,
				onClick: () => void controller?.startAutoBet(),
			}
		}

		if (isLive) {
			return {
				label: `Cash Out ${mine.currentMultiplier.toFixed(2)}×`,
				variant: 'default' as const,
				disabled: mine.isSubmitting || revealedGemCount === 0,
				onClick: () => void controller?.cashOut(),
			}
		}

		return {
			label: 'Bet',
			variant: 'default' as const,
			disabled: mine.isSubmitting,
			onClick: () => controller?.startManualGame(),
		}
	}, [controller, isAuthenticated, isAutoMode, isLive, mine.autoBetRunning, mine.autoSelectedPoints.length, mine.currentMultiplier, mine.isSubmitting, revealedGemCount])

	return (
		<div className={cn('flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-4', className)}>
			<Tabs value={mine.betMode} onValueChange={handleModeChange} className='shrink-0'>
				<TabsList className='grid h-11 w-full grid-cols-2 rounded-xl bg-secondary p-1'>
					<TabsTrigger value='manual' className='rounded-lg data-[state=active]:bg-background'>
						Manual
					</TabsTrigger>
					<TabsTrigger value='auto' className='rounded-lg data-[state=active]:bg-background'>
						Auto
					</TabsTrigger>
				</TabsList>
			</Tabs>

			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
			<div className={cn('space-y-2', controlsDisabled && 'pointer-events-none opacity-50')}>
				<div className='flex items-center justify-between gap-3'>
					<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Bet Amount</label>
					<span className='text-xs font-medium text-muted-foreground'>${formatBalance(usdAmount, 2)}</span>
				</div>
				<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
					<div className='relative min-w-0 flex-1'>
						<input
							type='text'
							inputMode='decimal'
							value={amountDraft}
							onChange={(event) => setAmountDraft(event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1'))}
							onBlur={commitAmount}
							className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
						/>
						<CurrencyIcon
							code={mine.currencyCode}
							symbolUrl={currency?.symbol_url}
							size='sm'
							className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'
						/>
					</div>
					<div className='flex w-[88px] shrink-0 border-l border-border'>
						<button type='button' onClick={() => handleQuickAmount(0.5)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							½
						</button>
						<span className='w-px bg-border' aria-hidden />
						<button type='button' onClick={() => handleQuickAmount(2)} className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent'>
							2×
						</button>
					</div>
				</div>
			</div>

			{!isLive || isAutoMode ? (
				<div className={cn('space-y-3', controlsDisabled && 'pointer-events-none opacity-50')}>
					<div className='flex items-center justify-between'>
						<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Mines</label>
						<span className='text-sm font-semibold text-foreground'>{mine.mineCount}</span>
					</div>
					<Slider min={MIN_MINES} max={MAX_MINES} step={1} value={[mine.mineCount]} onValueChange={(value) => dispatch(setMineCount(value[0] ?? MIN_MINES))} />
				</div>
			) : (
				<div className='grid grid-cols-2 gap-3 rounded-xl border border-border bg-secondary/60 px-4 py-3'>
					<div>
						<p className='text-xs uppercase tracking-wide text-muted-foreground'>Mines</p>
						<p className='mt-1 text-lg font-semibold text-foreground'>{mine.mineCount}</p>
					</div>
					<div>
						<p className='text-xs uppercase tracking-wide text-muted-foreground'>Safe Left</p>
						<p className='mt-1 text-lg font-semibold text-foreground'>{remainingSafeTiles}</p>
					</div>
				</div>
			)}

			{isLive && !isAutoMode ? (
				<div className='space-y-2'>
					<div className='flex items-center justify-between'>
						<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Total Profit</label>
						<span className='text-xs font-medium text-muted-foreground'>{mine.currentMultiplier.toFixed(2)}×</span>
					</div>
					<input
						value={formatBalance(mine.currentPayout, 4)}
						readOnly
						className='flex h-11 w-full rounded-xl border border-border bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
					/>
					<Button type='button' variant='outline' disabled={mine.isSubmitting} onClick={() => controller?.pickRandomTile()} className='h-11 w-full'>
						Pick Random Tile
					</Button>
				</div>
			) : null}

			{isAutoMode ? (
				<div className={cn('space-y-3', mine.autoBetRunning && 'pointer-events-none opacity-50')}>
					<div className='space-y-2'>
						<label className='text-xs text-muted-foreground'>Number of Bets (0 = ∞)</label>
						<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
							<input
								type='text'
								inputMode='numeric'
								value={autoBetCountDraft}
								disabled={mine.autoBetRunning}
								onChange={(event) => {
									const next = event.target.value.replace(/[^\d]/g, '')
									setAutoBetCountDraft(next)
									const parsed = Number(next)
									dispatch(
										setMineAutoBetSettings({
											betCount: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
											isInfinite: parsed === 0,
										}),
									)
								}}
								className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none disabled:opacity-50 sm:text-base sm:tracking-[-0.24px]'
							/>
						</div>
					</div>
					<div className='space-y-2'>
						<label className='text-xs text-muted-foreground'>Stop on Profit</label>
						<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
							<input
								type='text'
								inputMode='decimal'
								value={stopOnProfitDraft}
								disabled={mine.autoBetRunning}
								onChange={(event) => {
									const next = event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
									setStopOnProfitDraft(next)
									const parsed = Number(next)
									if (Number.isFinite(parsed)) {
										dispatch(setMineAutoBetSettings({ stopOnProfit: Math.max(0, parsed) }))
									}
								}}
								className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none disabled:opacity-50 sm:text-base sm:tracking-[-0.24px]'
							/>
						</div>
					</div>
					<div className='space-y-2'>
						<label className='text-xs text-muted-foreground'>Stop on Loss</label>
						<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
							<input
								type='text'
								inputMode='decimal'
								value={stopOnLossDraft}
								disabled={mine.autoBetRunning}
								onChange={(event) => {
									const next = event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
									setStopOnLossDraft(next)
									const parsed = Number(next)
									if (Number.isFinite(parsed)) {
										dispatch(setMineAutoBetSettings({ stopOnLoss: Math.max(0, parsed) }))
									}
								}}
								className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none disabled:opacity-50 sm:text-base sm:tracking-[-0.24px]'
							/>
						</div>
					</div>
				</div>
			) : null}
			</div>

			<div className='shrink-0'>
				<Button type='button' variant={action.variant} disabled={action.disabled || !controller} onClick={action.onClick} className='h-12 w-full text-base font-semibold'>
					{action.label}
				</Button>
			</div>
		</div>
	)
}
